import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Hash, Users, TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { TopicStats } from '../model/contracts'

type Variant = 'a' | 'b'

const BORDER: Record<Variant, string> = {
  a: 'border-primary/40',
  b: 'border-accent/40',
}

const LABEL_COLOR: Record<Variant, string> = {
  a: 'text-primary',
  b: 'text-accent-foreground',
}

export const TopicStatsCard = memo(function TopicStatsCard({
  stats,
  variant,
  loading,
}: {
  stats: TopicStats | null
  variant: Variant
  loading?: boolean
}) {
  const { t } = useTranslation('compare')

  if (loading) {
    return (
      <div className="flex-1 rounded-xl border border-border bg-card p-4 animate-pulse">
        <div className="mb-3 h-5 w-32 rounded bg-secondary" />
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-secondary" />
              <div className="h-4 w-24 rounded bg-secondary" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-border bg-card p-6">
        <p className="text-sm text-muted-foreground">{t('empty')}</p>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex-1 rounded-xl border-2 bg-card p-4 shadow-sm transition-shadow hover:shadow-md',
        BORDER[variant],
      )}
    >
      <h3 className={cn('mb-3 truncate text-sm font-bold', LABEL_COLOR[variant])}>
        {stats.name}
      </h3>
      <ul className="space-y-2">
        <li className="flex items-center gap-2 text-sm text-foreground">
          <Hash size={14} className="shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">{t('totalMentions')}</span>
          <span className="ml-auto font-semibold">
            {stats.totalMentions.toLocaleString()}
          </span>
        </li>
        <li className="flex items-center gap-2 text-sm text-foreground">
          <Users size={14} className="shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">{t('uniqueAuthors')}</span>
          <span className="ml-auto font-semibold">
            {stats.uniqueAuthors.toLocaleString()}
          </span>
        </li>
        <li className="flex items-center gap-2 text-sm text-foreground">
          <TrendingUp size={14} className="shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">{t('positiveSentiment')}</span>
          <span className="ml-auto font-semibold">{stats.positivePct}%</span>
        </li>
      </ul>
    </div>
  )
})
